// /src/stores/FormStore.js
import { defineStore } from "pinia";
import { reactive, ref, computed, watch } from "vue";

const STORAGE_KEY = "formStore:v2";
const STORE_VERSION = 2;

export const useFormStore = defineStore("form", () => {
  // ---------- PRIVATE ----------
  // Not exposed in the returned API
  const leadUploader = ref(null); // { id, name, lastname, email?, institute? }

  // ---------- STATE ----------
  const formState = reactive({
    version: STORE_VERSION,
    upload_type: "",
    title: "",
    description: "",
    is_open: true,
    uploaders: [], // [{ id, name, lastname, ... }]
    file: null,
    filename: "",
    structureDict: ref({}),
    file_changed_from_preview: false,
    preview_needs_refresh: false, // Flag to indicate preview should be regenerated (title changed, etc.)
  });

  const currentPage = ref(1);
  const maxPage = ref(3);

  // ---------- DERIVED ----------
  const isFirstPage = computed(() => currentPage.value <= 1);
  const isLastPage = computed(() => currentPage.value >= maxPage.value);
  const progressPct = computed(() =>
    Math.round((currentPage.value / maxPage.value) * 100)
  );
  const hasFile = computed(() => !!formState.file && !!formState.filename);
  const isTitleValid = computed(() => formState.title.trim().length > 0);
  const isUploadTypeValid = computed(
    () => formState.upload_type.trim().length > 0
  );
  const isDescriptionValid = computed(
    () => formState.description.trim().length >= 300
  );

  function isStepValid(step = currentPage.value) {
    switch (step) {
      case 1:
        return isUploadTypeValid.value && isTitleValid.value && hasFile.value;
      case 2:
        return isDescriptionValid.value;
      default:
        return true;
    }
  }

  // ---------- INTERNAL HELPERS ----------
  function sanitizeUser(u) {
    if (!u || typeof u !== "object") return null;
    const { id, name = "", lastname = "", email = "", institute = "" } = u;
    if (id == null) return null;
    return { id, name, lastname, email, institute };
  }

  function ensureLead() {
    if (!leadUploader.value) return;
    const lead = leadUploader.value;
    const rest = (formState.uploaders || []).filter((u) => u?.id !== lead.id);
    formState.uploaders = [lead, ...rest];
  }

  // ---------- ACTIONS: NAV ----------
  function nextPage() {
    if (!isStepValid()) return false;
    if (!isLastPage.value) currentPage.value += 1;
    return true;
  }
  function prevPage() {
    if (!isFirstPage.value) currentPage.value -= 1;
  }
  function goToPage(page) {
    const n = Number(page) || 1;
    currentPage.value = Math.min(Math.max(1, n), maxPage.value);
  }

  // ---------- ACTIONS: MUTATORS ----------
  function patch(payload = {}) {
    const allowed = [
      "upload_type",
      "title",
      "description",
      "is_open",
      "filename",
      "file_changed_from_preview",
      "preview_needs_refresh",
    ];
    for (const k of allowed) {
      if (Object.prototype.hasOwnProperty.call(payload, k)) {
        formState[k] = payload[k];
      }
    }
    if (Array.isArray(payload.uploaders)) {
      // de-dupe by id
      const map = new Map();
      for (const u of payload.uploaders) {
        const s = sanitizeUser(u);
        if (s) map.set(s.id, s);
      }
      formState.uploaders = [...map.values()];
      ensureLead();
    }
    if (payload.structureDict && typeof payload.structureDict === "object") {
      formState.structureDict.value = payload.structureDict;
    }
    if (Object.prototype.hasOwnProperty.call(payload, "file")) {
      formState.file = payload.file;
    }
  }

  function sanitizeTitle() {
    formState.title = formState.title.replace(/\s+/g, " ").trim();
  }

  function setFile(file, filename = "") {
    formState.file = file || null;
    formState.filename = filename || (file && file.name) || "";
    formState.file_changed_from_preview = true;
  }

  function clearFile() {
    formState.file = null;
    formState.filename = "";
    formState.file_changed_from_preview = true;
  }

  function setStructure(structure) {
    formState.structureDict.value = structure || {};
  }
  function resetStructure() {
    formState.structureDict.value = {};
  }

  function setUploaders(list) {
    const map = new Map();
    (Array.isArray(list) ? list : []).forEach((u) => {
      const s = sanitizeUser(u);
      if (s) map.set(s.id, s);
    });
    formState.uploaders = [...map.values()];
    ensureLead();
  }

  function addUploader(uploader) {
    const s = sanitizeUser(uploader);
    if (!s) return;
    const map = new Map(formState.uploaders.map((u) => [u.id, u]));
    map.set(s.id, s);
    formState.uploaders = [...map.values()];
    ensureLead();
  }

  function removeUploader(index) {
    // Prevent removing lead (index 0) if we have a lead set
    if (leadUploader.value && index === 0) return;
    if (index < 0 || index >= formState.uploaders.length) return;
    formState.uploaders = formState.uploaders.filter((_, i) => i !== index);
    ensureLead();
  }

  // ---------- LEAD (LOGGED USER) ----------
  function setLeadUploader(user) {
    const s = sanitizeUser(user);
    if (!s) return;
    leadUploader.value = s;
    ensureLead();
  }

  // ---------- RESET ----------
  function resetAll() {
    formState.upload_type = "";
    formState.title = "";
    formState.description = "";
    formState.is_open = true;
    formState.file = null;
    formState.filename = "";
    formState.structureDict.value = {};
    formState.file_changed_from_preview = false;
    formState.preview_needs_refresh = false;
    currentPage.value = 1;

    // keep lead user first if present
    formState.uploaders = leadUploader.value ? [leadUploader.value] : [];
  }

  // ---------- PERSISTENCE ----------
  function saveToStorage() {
    try {
      const payload = {
        version: STORE_VERSION,
        state: {
          upload_type: formState.upload_type,
          title: formState.title,
          description: formState.description,
          is_open: formState.is_open,
          uploaders: formState.uploaders, // includes lead (that's OK)
          filename: formState.filename,
          structureDict: formState.structureDict.value,
          file_changed_from_preview: formState.file_changed_from_preview,
          preview_needs_refresh: formState.preview_needs_refresh,
          currentPage: currentPage.value,
        },
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn("Failed to persist form store:", e);
    }
  }

  function loadFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== STORE_VERSION) return;
      const s = parsed.state || {};
      patch(s);
      currentPage.value = s.currentPage || 1;
      // ensure lead (if set earlier) is placed first
      ensureLead();
    } catch (e) {
      console.warn("Failed to load form store:", e);
    }
  }

  function clearStorage() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }

  // Auto-save on changes (throttled)
  let saveTimer = null;
  function scheduleSave() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(saveToStorage, 250);
  }
  watch(
    () => [
      formState.upload_type,
      formState.title,
      formState.description,
      formState.is_open,
      formState.uploaders,
      formState.filename,
      formState.structureDict.value,
      formState.file_changed_from_preview,
      formState.preview_needs_refresh,
      currentPage.value,
    ],
    scheduleSave,
    { deep: true }
  );

  // ---------- API ----------
  return {
    // state
    formState,
    currentPage,
    maxPage,

    // derived
    isFirstPage,
    isLastPage,
    progressPct,
    hasFile,
    isTitleValid,
    isUploadTypeValid,
    isDescriptionValid,
    isStepValid,

    // nav
    nextPage,
    prevPage,
    goToPage,

    // mutators
    patch,
    sanitizeTitle,
    setFile,
    clearFile,
    setStructure,
    resetStructure,
    setUploaders,
    addUploader,
    removeUploader,

    // lead user (NOT exposing the ref, only setter)
    setLeadUploader,

    // reset & persistence
    resetAll,
    saveToStorage,
    loadFromStorage,
    clearStorage,
  };
});
